import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Every page opens from the TOP (client, 2026-09-09).
 *
 * Instant scroll reset on SPA route changes, re-asserted for ~1.2s so nothing
 * that happens after the swap can drag the viewport back down: the menu
 * closing (0.5s), Suspense swapping a lazy page in, the Hero/Creed late
 * ScrollTrigger.refresh() calls (~900ms), Safari clamping to a shorter
 * document. The re-asserts stop the moment the user scrolls themselves.
 *
 * Hash links (/events/ultimate-lap#hof) land on the section instead.
 * history.scrollRestoration is manual so a browser never re-restores an old
 * offset on back/forward after we've reset it.
 */
export function hardScrollTop() {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

const CANCEL_EVENTS = ["wheel", "touchmove", "keydown"];

/** Reset to top now and keep it there for `ms`, unless the user scrolls. */
export function pinToTop(ms = 1200) {
  return pinTo(() => 0, ms);
}

/** Same, but to an element's top (minus the fixed nav) - for a form that lives
 *  mid-page (Register interest on the microsites). */
export function pinToElement(el, ms = 1200) {
  if (!el) return pinToTop(ms);
  const navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--nav-h")) || 76;
  return pinTo(() => Math.max(0, el.getBoundingClientRect().top + window.scrollY - navH - 16), ms);
}

function pinTo(getTop, ms) {
  let cancelled = false;
  const stop = () => { cancelled = true; };
  const go = () => { const t = getTop(); window.scrollTo(0, t); if (t === 0) hardScrollTop(); };
  CANCEL_EVENTS.forEach((e) => window.addEventListener(e, stop, { passive: true, once: true }));
  go();
  const raf = requestAnimationFrame(go);
  const timers = [60, 200, 450, 700, 1000, ms].map((t) => setTimeout(() => { if (!cancelled) go(); }, t));
  return () => {
    cancelAnimationFrame(raf);
    timers.forEach(clearTimeout);
    CANCEL_EVENTS.forEach((e) => window.removeEventListener(e, stop));
  };
}

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const prevPath = useRef(null);

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    const samePage = prevPath.current === pathname;
    prevPath.current = pathname;
    const cleanups = [];
    if (!hash) cleanups.push(pinToTop());
    else if (!samePage) hardScrollTop();

    const r1 = requestAnimationFrame(() => {
      try { ScrollTrigger.refresh(); } catch { /* noop */ }
    });
    const t2 = setTimeout(() => {
      try { ScrollTrigger.refresh(); } catch { /* noop */ }
    }, 350);
    // In-app link to a section on another page (e.g. /events/ultimate-lap#hof):
    // the browser only honours hashes on full loads, so land there ourselves.
    // Instant jumps (smooth scrolls get cancelled by ScrollTrigger.refresh);
    // re-assert once after the Hero's late refresh (~900ms) settles layout.
    const jump = () => {
      if (!hash) return;
      const el = document.getElementById(hash.slice(1));
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(el).scrollMarginTop) || 0);
      window.scrollTo({ top, behavior: "auto" });
    };
    if (samePage && hash) jump(); // in-page anchor: land now, then re-assert
    const t3 = setTimeout(jump, 380);
    const t4 = setTimeout(jump, 1100);
    return () => {
      cleanups.forEach((fn) => fn());
      cancelAnimationFrame(r1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [pathname, hash]);

  return null;
}
